import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output
} from "@angular/core";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "edit-items",
  templateUrl: "./edit-items.component.html",
  styleUrls: ["./edit-items.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditItemsComponent implements OnChanges, OnDestroy {
  @Input() itemDetailsForm: FormArray;
  @Input() order: any;
  @Input() reverseQCEnabled: boolean = false;
  @Input() reverseQCForm: FormGroup;
  @Output() removeItemEvent = new EventEmitter();
  @Output() reverseQCChange = new EventEmitter<boolean>();
  @Output() uploadImageEvent = new EventEmitter<{ file: File; itemIndex: number }>();
  @Output() addImageUrlEvent = new EventEmitter<{ url: string; itemIndex: number }>();
  @Output() removeImageEvent = new EventEmitter<{ itemIndex: number; imageIndex: number }>();
  awbNumber: string;
  
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(private _cd: ChangeDetectorRef) {}

  ngOnChanges() {
    this.awbNumber = this.order?.awb || this.order?.originalOrderAWB;
    
    // Sync product_name with name field when Reverse QC is enabled
    if (this.reverseQCEnabled && this.itemDetailsForm) {
      for (let i = 0; i < this.itemDetailsForm.length; i++) {
        const itemGroup = this.itemDetailsForm.at(i) as FormGroup;
        const nameControl = itemGroup.get('name');
        const productNameControl = itemGroup.get('product_name');
        
        if (nameControl && productNameControl) {
          // Set initial value
          productNameControl.setValue(nameControl.value, { emitEvent: false });
          
          // Subscribe to changes
          nameControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
              productNameControl.setValue(value, { emitEvent: false });
            });
        }
      }
    }
    
    this._cd.markForCheck();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  markFormAsTouched() {
    this.itemDetailsForm.markAsTouched();
    this._cd.markForCheck();
  }

  triggerChangeDetection() {
    this._cd.markForCheck();
  }

  onReverseQCCheckboxChange(event: any) {
    this.reverseQCChange.emit(event.checked);
    this._cd.markForCheck();
  }

  getProductImages(itemIndex: number): FormArray {
    const item = this.itemDetailsForm.at(itemIndex) as FormGroup;
    return item.get("product_images") as FormArray;
  }

  onFileSelected(event: any, itemIndex: number) {
    const file = event.target.files[0];
    if (file) {
      this.uploadImageEvent.emit({ file, itemIndex });
    }
    // Reset file input
    event.target.value = '';
  }

  onAddImageUrl(itemIndex: number, urlInput: HTMLInputElement) {
    const url = urlInput.value.trim();
    if (url) {
      this.addImageUrlEvent.emit({ url, itemIndex });
      urlInput.value = "";
      this._cd.markForCheck();
    }
  }

  onRemoveImage(itemIndex: number, imageIndex: number) {
    this.removeImageEvent.emit({ itemIndex, imageIndex });
    this._cd.markForCheck();
  }

  onImageError(event: any) {
    // Set a placeholder image on error
    event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgRXJyb3I8L3RleHQ+PC9zdmc+';
    event.target.style.opacity = '0.5';
  }
}
